import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import type { InvoiceActionResult } from 'src/interfaces/invoice'

interface Props {
  results: InvoiceActionResult[]
}

const InvoiceActionResultsStepComponent = ({ results }: Props) => {
  const failedResults = useMemo(() => {
    return results.filter((result) => {
      return !result.ok
    })
  }, [results])

  const succeededCount = results.length - failedResults.length

  // A run-level failure (e.g. missing partnerId, nothing to act on) keeps `results: []` in the
  // store. Without this branch, `failedResults.length === 0` would fall into the success copy
  // below and read "All 0 partner invoices were updated successfully" under a red error toast.
  if (results.length === 0) {
    return (
      <div css={tw`flex flex-col gap-3`}>
        <Typography variant="body2" css={tw`text-critical-500`}>
          The action could not be started. Nothing was changed.
        </Typography>
      </div>
    )
  }

  let headline: string

  if (failedResults.length === 0) {
    headline = `All ${succeededCount} partner ${
      succeededCount === 1 ? 'invoice was' : 'invoices were'
    } updated successfully.`
  } else {
    headline = `${failedResults.length} of ${results.length} could not be processed and ${
      failedResults.length === 1 ? 'was' : 'were'
    } left unchanged. The rest were updated.`
  }

  return (
    <div css={tw`flex flex-col gap-3`}>
      {/* The headline states the real split. Never "Done" over a run that partly failed. */}
      <Typography variant="body2">{headline}</Typography>
      <div
        css={tw`flex max-h-[280px] flex-col divide-y divide-solid divide-shades-100 overflow-y-auto rounded-lg border border-solid border-shades-100`}
        data-testid="invoice-action-results"
      >
        {results.map((result) => {
          return (
            <div
              key={result.invoiceId}
              css={tw`flex flex-row items-center justify-between gap-4 px-4 py-2`}
            >
              <Typography variant="body2" css={tw`truncate`}>
                {result.label}
              </Typography>
              {/* The backend's own message, verbatim — it is the only thing that says why. */}
              <Typography
                variant="body2"
                css={result.ok ? tw`text-success-500` : tw`text-critical-500`}
              >
                {result.ok ? 'Updated' : result.message || 'Failed'}
              </Typography>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const InvoiceActionResultsStep = memo(InvoiceActionResultsStepComponent)
